/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  InputLabel,
  Modal,
  TextField,
  Fade,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import * as Style from './styles';
import { KeyboardEventHandler, useEffect, useRef, useState } from 'react';

import { LOGO } from '../../config';
import { API } from '../../services/api';
import { AxiosError } from 'axios';
import { useStore } from '../../context/Store';

export function VoucherModalButton() {
  const {
    changeNotification,
    changeVoucherId,
    changePlate,
    changeFuels,
    changeValueRefueling,
    changeUserAuthenticated,
  } = useStore();

  const [isOpen, setIsOpen] = useState(false);
  const [code, setCode] = useState('');
  const [receipt, setReceipt] = useState<string | null>(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const codeRef = useRef<HTMLInputElement | null>(null);
  const receiptRef = useRef<HTMLDivElement | null>(null);

  const deleteCode: KeyboardEventHandler<HTMLDivElement> = event => {
    if (event?.key === 'Backspace') {
      setCode(prev => prev.slice(0, -1));
    }
  };

  const closeModal = () => {
    setCode('');
    setReceipt(null);
    setShowReceipt(false);
    setReceiptLoading(false);
    setIsOpen(false);
  };

  const handleReceiptImageLoad = () => {
    setReceiptLoading(false);
  };

  const decodeHtml = (html: string) => {
    // Simple decode using textarea - preserves all HTML content exactly as API sends
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  };

  const handlePrint = () => {
    if (!receipt) return;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(decodeHtml(receipt));
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  const handleEntered = () => {
    setTimeout(() => {
      codeRef.current?.focus();
    }, 600);
  };

  const handleVoucherCodeSubmit = async () => {
    const idPosto = localStorage.getItem('idPosto');

    try {
      const response = await API.get(
        `/api/v1/Voucher/validar-abastecimento?IdPosto=${idPosto}&CodVoucher=${code.toUpperCase()}`,
      );

      if (response.data.isValid) {
        const { voucherId, placa, listCombustivel, valor, comprovante } =
          response.data;

        // Always store voucher data
        changeVoucherId(voucherId);
        changePlate(placa);
        changeFuels(listCombustivel);
        changeValueRefueling(String(valor));

        if (comprovante) {
          // Store receipt for later use after refueling
          console.log('Raw HTML from API:', comprovante);
          console.log('Decoded HTML:', decodeHtml(comprovante));
          localStorage.setItem('voucherReceipt', comprovante);
        }

        // Always go to refueling after voucher validation
        changeUserAuthenticated(true);
        closeModal();
      }
    } catch (error: AxiosError | any) {
      closeModal();

      const errors = error?.response?.data?.errors;
      if (errors) {
        changeNotification('error', errors.join('\n'));
      } else {
        changeNotification('error', 'Erro no aplicativo, tente novamente!.');
      }
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    function handleKeyPress(event: KeyboardEvent) {
      if (event.key === 'Enter' && code) {
        handleVoucherCodeSubmit();
      }
    }

    document.addEventListener('keypress', handleKeyPress);

    return () => {
      document.removeEventListener('keypress', handleKeyPress);
    };
  }, [code, isOpen, handleVoucherCodeSubmit]);

  useEffect(() => {
    if (!receiptLoading) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data === 'logoLoaded') {
        console.log('Logo loaded successfully');
        setReceiptLoading(false);
      }
    };

    // Fallback timeout in case logo doesn't load
    const timeout = setTimeout(() => {
      console.log('Logo load timeout - showing receipt anyway');
      setReceiptLoading(false);
    }, 5000);

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timeout);
    };
  }, [receiptLoading]);

  return (
    <>
      <Style.Button onClick={() => setIsOpen(true)}>
        CLIQUE AQUI <br /> PARA VOUCHER
      </Style.Button>

      <Modal
        open={isOpen}
        onClose={closeModal}
        closeAfterTransition
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Fade in={isOpen} onEntered={handleEntered}>
          <Box sx={Style.Modal}>
            {showReceipt && receipt ? (
              <Box
                sx={{
                  width: '100%',
                  maxHeight: '70vh',
                  overflow: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '1rem',
                }}
              >
                {receiptLoading && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '200px',
                      gap: '1rem',
                    }}
                  >
                    <CircularProgress />
                    <Typography>Carregando logo do comprovante...</Typography>
                  </Box>
                )}
                <div
                  ref={receiptRef}
                  dangerouslySetInnerHTML={{
                    __html: decodeHtml(receipt).replace(
                      /<img([^>]*class=["']logo-img["'][^>]*)>/g,
                      `<img$1 onload="window.parent.postMessage('logoLoaded', '*')" onerror="window.parent.postMessage('logoLoaded', '*')">`,
                    ),
                  }}
                  style={{
                    width: '100%',
                    backgroundColor: '#fff9e6',
                    borderRadius: '8px',
                    padding: '8px',
                    display: receiptLoading ? 'none' : 'block',
                  }}
                />
                {!receiptLoading && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '1rem',
                      width: '100%',
                      justifyContent: 'space-around',
                    }}
                  >
                    <Style.BackButton onClick={closeModal}>
                      Fechar
                    </Style.BackButton>
                    <Style.SubmitButton onClick={handlePrint}>
                      Imprimir
                    </Style.SubmitButton>
                    <Style.SubmitButton
                      onClick={() => {
                        changeUserAuthenticated(true);
                        closeModal();
                      }}
                    >
                      Continuar
                    </Style.SubmitButton>
                  </Box>
                )}
              </Box>
            ) : (
              <>
                <Style.Logo src={LOGO} />

                <InputLabel
                  sx={{
                    textTransform: 'uppercase',
                    fontSize: '30px',
                    color: '#000',
                    fontWeight: '600',
                    textAlign: 'center',
                  }}
                >
                  Digite no campo abaixo <br /> o código do voucher:
                </InputLabel>

                <TextField
                  inputRef={codeRef}
                  value={code}
                  onChange={event => setCode(event.target.value)}
                  onKeyDown={deleteCode}
                  inputProps={{
                    autoComplete: 'off',
                  }}
                  sx={{
                    background: '#fff',
                    borderRadius: '7px',
                    width: '80%',
                    '& .MuiOutlinedInput-input': {
                      height: '70px',
                      fontSize: '56px',
                      textAlign: 'center',
                      background: '#FAEF00',
                      color: '#000',
                      textTransform: 'uppercase',
                    },
                  }}
                />

                <Style.ButtonsContainer>
                  <Style.BackButton onClick={closeModal}>
                    Voltar
                  </Style.BackButton>

                  <Style.SubmitButton
                    disabled={!code}
                    onClick={handleVoucherCodeSubmit}
                  >
                    Ok
                  </Style.SubmitButton>
                </Style.ButtonsContainer>
              </>
            )}
          </Box>
        </Fade>
      </Modal>
    </>
  );
}
